// FaceLock Ink Scan: when a close-up of a tattoo or a mark is good enough to
// keep. The page looks at a small colour copy of the frame several times a
// second: measureInk reads its centre (light, shine, sharpness, a tiny
// thumbnail), the finder (ink-find.ts) says where the mark is, and InkJudge
// says when to take the picture:
//
//   * the mark is found on skin and LOCKED ON: found in 2 of the last 3
//     looks in the same place (in a hairy frame: every one of the 3), dropped
//     only after 4 misses in a row (owner, 2026-10-07: on his phone it rushed
//     and kept pictures with no tattoo in them; it must locate the tattoo and
//     lock on to it). Stray wisps of hair found here and there are not a mark.
//     A scar may be too pale to find: one that is found is framed like a
//     tattoo; one that is not is taken on skin alone after a while, never on
//     a wall or a shirt;
//   * on the right side of the face, when the face model saw it (ink-face.ts);
//   * in the box (centred) and big enough in the frame; the wide view shows
//     the area around it, the mark still big enough to be the mark (a speck
//     is not);
//   * light: not too dark (very dark skin is not a dark room), not blown out;
//   * no shine on the skin (glare: a share of blown-out pixels);
//   * never before INK.settleMs into a view, nor LOCKJ.lockSettleMs after the
//     lock: the person gets the mark into the box and the camera focuses on
//     it (owner, 2026-10-07: it fired before he was in place and before it
//     focused; most pictures had to be retaken);
//   * held still for INK.steadyMs (the small thumbnail barely changes and the
//     mark's box does not jump), and the mark found in the look that takes it
//     and a moment before (2 of the last 3 finder runs): a lock held through
//     a miss or two is not the mark in the frame (on a slow phone the bare
//     skin after it was kept, 2026-10-07 review);
//   * sharp: close to the sharpest this view has shown since the lock, the
//     settle included, so the camera has had time to find its focus (the
//     floor relaxes with time, so a soft front camera still finishes);
//   * the second close-up is the same mark, a little to the side: it must
//     differ from the first (relaxed with time too). A lock carried over from
//     the first close-up stands for "the same mark" only while every look
//     finds it where it was, looking as it did: a jump, a miss or another
//     look (ink colour, darkness, fill) and the marks are compared.
//
// The framing rules relax with time; being found never does. No picture is
// taken automatically without a lock on the mark (or, for a scar, skin in the
// box). The scan still always finishes (owner rule: completion matters as
// much as accuracy; never an impossible requirement): "Take it now" always
// works, and past INK.countdownAtMs a 3-2-1 countdown takes it, but only with
// a lock held for LOCKJ.holdMs on one mark (a box that keeps jumping is not
// one mark); an empty frame never counts down. From
// INK.stuckAtMs a view that cannot lock says so and points at "Take it now".
// The person is told what to change in plain words; the camera is never blamed.

import { laplacianVariance, regionStats, thumbnail, type Gray } from "./pixels";
import { LOCK, iou, sameMark, type Box01, type InkFind, type InkSignature, type MarkKind } from "./ink-find";
import type { InkView, MarkRegion, MarkSide } from "@/shared/face-qc/marks.mjs";

export const INK = {
  /** Mean brightness (0..1) of the centre. */
  minMean: 0.16,
  maxMean: 0.9,
  /** Share of blown-out pixels in the centre: shine on the skin. */
  maxGlare: 0.03,
  /** No picture this soon into a view: time to get in place and for the camera to focus. */
  settleMs: 3000,
  /** Held still this long before the picture is taken. */
  steadyMs: 1200,
  /** Mean thumbnail change between two looks that still counts as still. */
  steadyDiff: 0.03,
  /** Sharpness relative to the sharpest seen on this view, and when it (and the framing) relaxes. */
  sharpShare: [0.88, 0.72, 0.55] as const,
  relaxAtMs: [12000, 20000] as const,
  /** The second close-up differs at least this much from the first (relaxed at sameRelaxMs). */
  minAngleDiff: 0.035,
  sameRelaxMs: 14000,
  /** Past this, a 3-2-1 countdown takes the picture, with a lock held (LOCKJ.holdMs). */
  countdownAtMs: 30000,
  /** Past this, a view that cannot lock asks for "Take it now". */
  stuckAtMs: 20000,
  /** A dark frame is "dark" only when its skin is darker than this (very dark skin in good light is not). */
  darkSkinMin: 0.1,
} as const;

/** The lock on the mark: how it is gained, held, framed and shown. */
export const LOCKJ = {
  /**
   * Gained when found in inOf[0] of the last inOf[1] finder runs, in the same place (the boxes do not jump between
   * them, jumpIou), and in a frame the finder calls hairy in all inOf[1]; lost after outAfter misses in a row (0.5 s
   * when the finder runs on every look). Stray wisps of hair found 2 looks in 3, each somewhere else, held a lock; and
   * not "no lock in a hairy frame": a faded tattoo and a tattoo on a hairy forearm are found in frames called hairy.
   */
  inOf: [2, 3] as const,
  outAfter: 4,
  /** After the lock is gained: time for the focus to land on the mark. */
  lockSettleMs: 1500,
  /** The countdown needs a lock held this long, on one mark (the box has not jumped for as long). */
  holdMs: 2000,
  /** The mark's box moved this much between looks (IoU below): it jumped, the stillness starts over. */
  jumpIou: 0.3,
  /** The box shown blends this much of the old box while the two overlap (IoU at least smoothIou). */
  smooth: 0.5,
  smoothIou: 0.3,
  /** The mark's size changed this much: the sharpest seen starts over (a new framing). */
  reframe: 0.35,
  /** Centred: the mark's centre within this of the frame's centre (per stage); never past 0.20 on the face (an eyebrow is near). */
  centre: { body: [0.15, 0.2, 0.25] as const, face: [0.15, 0.18, 0.2] as const },
  /** ...or its box covers the square of this half-size round the centre (a big mark is centred). */
  core: 0.1,
  /** Close-ups: the mark's long side over the frame's short side, at least (per stage). Front cameras cannot focus close: smaller on the face. */
  span: {
    body: { tattoo: [0.45, 0.33, 0.25], birthmark: [0.45, 0.33, 0.25], scar: [0.35, 0.27, 0.2], mole: [0.08, 0.06, 0.045] },
    face: { tattoo: [0.14, 0.1, 0.07], birthmark: [0.14, 0.1, 0.07], scar: [0.12, 0.09, 0.06], mole: [0.035, 0.03, 0.025] },
  } as Record<"body" | "face", Record<MarkKind, readonly [number, number, number]>>,
  /** The wide view: the mark at most this big (per stage), and this much skin in view. */
  wideMaxSpan: [0.75, 0.9, Infinity] as const,
  wideMinSkin: 0.35,
  /**
   * ...and at least this big (per stage; a quarter of the close-up's floor on the body, a third on the face): a speck
   * found in a wide view is not the mark (the wide view had no size floor, and a small centred stray hair passed every
   * framing rule; 2026-10-07).
   */
  wideMinSpan: {
    body: { tattoo: [0.1, 0.08, 0.06], birthmark: [0.1, 0.08, 0.06], scar: [0.08, 0.06, 0.05], mole: [0.02, 0.015, 0.012] },
    face: { tattoo: [0.05, 0.04, 0.03], birthmark: [0.05, 0.04, 0.03], scar: [0.04, 0.03, 0.025], mole: [0.012, 0.01, 0.008] },
  } as Record<"body" | "face", Record<MarkKind, readonly [number, number, number]>>,
  /** A scar not found by then is taken on skin alone, with at least scarSkinBox of the box skin; under scarNoSkinBox it is "noskin". */
  scarSkinOnlyMs: 10000,
  scarSkinBox: 0.7,
  scarNoSkinBox: 0.5,
  /** The face model's side (IODs from the midline, + = the person's left): how far over the midline a side mark may sit; how far off it a centre mark may. */
  sideTol: 0.15,
  centreSideMax: 0.6,
} as const;

export type InkProblem =
  | "dark" | "bright" | "glare" | "noskin" | "nomark" | "wrongside" | "centre" | "closer" | "back"
  | "moving" | "notsame" | "same" | "blur" | "settling";

/** What measureInk reads off the look copy's centre. */
export interface InkMeasure {
  t: number;
  sharp: number;
  mean: number;
  glare: number;
  thumb: number[];
}

/** One look, as the judge takes it. */
export interface InkSample extends InkMeasure {
  /** Where the mark is (ink-find.ts). */
  mark: InkFind;
  /** The finder ran on this look (false: its last result was reused on a slow phone; the lock does not move). */
  fresh: boolean;
  /** The mark's side from a fresh face fix (ink-face.ts sideOf), else null. */
  side: number | null;
  /** The mark is below the chin, from a fresh face fix (ink-face.ts belowChin), else null. */
  belowChin: boolean | null;
}

/** The centre 60% of a frame: where the person is asked to put the mark. */
export function inkRegion(g: Pick<Gray, "w" | "h">) {
  return { x: g.w * 0.2, y: g.h * 0.2, w: g.w * 0.6, h: g.h * 0.6 };
}

export function measureInk(g: Gray, t: number): InkMeasure {
  const r = inkRegion(g);
  const st = regionStats(g, r);
  return { t, sharp: laplacianVariance(g, r), mean: st.mean, glare: st.over, thumb: thumbnail(g, 8, r) };
}

export function thumbDiff(a: number[] | null | undefined, b: number[] | null | undefined): number {
  if (!a || !b || a.length !== b.length || !a.length) return 1;
  let s = 0;
  for (let i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
  return s / a.length;
}

export type InkLock = "searching" | "found" | "locked";

export interface InkVerdict {
  ready: boolean;
  problem: InkProblem | null;
  /** Past INK.countdownAtMs. */
  overdue: boolean;
  /** searching: no lock; found: locked on, with something to change; locked: locked on, nothing but the wait. */
  lock: InkLock;
  /** The mark's box for the screen (smoothed; fractions of the UNMIRRORED frame), null without a lock. */
  box: Box01 | null;
  /** The centring tolerance now: the guide box on screen is the centre plus or minus this. */
  tol: number;
  /** The page may count 3-2-1 and take it (overdue, a lock held LOCKJ.holdMs, nothing but framing or the wait left). */
  countdown: boolean;
  /** Cannot lock (or frame it) on its own for a while: show "Take it now" plainly. */
  stuck: boolean;
  /** How long the lock has been held on one mark, ms: since it was gained or its box last jumped (0 without one). */
  lockHeldMs: number;
  /** The mark is in THIS look (found, or a scar taken on skin alone): a picture taken now shows it. */
  seen: boolean;
  /** A scar not found, taken on skin alone (the page says so). */
  skinOnly: boolean;
}

export interface InkJudgeOptions {
  view: InkView;
  kind: MarkKind;
  region: MarkRegion;
  /** The place's side: the face model checks it. */
  side: MarkSide;
  /** close_b: the first close-up's thumbnail and mark signature. */
  ref?: { thumb: number[] | null; sig: InkSignature | null } | null;
  /** close_b after close_a was taken on a lock: the lock carries on (same mark, no "notsame" check until it is lost). */
  carry?: { box: Box01 } | null;
}

/** Locked on with nothing left but the wait. */
const WAITING: ReadonlySet<InkProblem | null> = new Set<InkProblem | null>([null, "settling", "moving", "blur", "same"]);
/** The countdown may run over these (framing that relaxes, or the wait); never over a missing or wrong mark. */
const COUNTABLE: ReadonlySet<InkProblem | null> = new Set<InkProblem | null>([null, "settling", "moving", "blur", "same", "closer", "back"]);
/** What a person cannot fix by waiting: from INK.stuckAtMs, "Take it now". */
const STUCK: ReadonlySet<InkProblem | null> = new Set<InkProblem | null>(["noskin", "nomark", "centre", "wrongside", "notsame"]);

/**
 * The face model checks this place's side: not the back, not the top of the head, never the body. The page runs the
 * face model only where it checks the side or points at the named spot (ink-face.ts anchors).
 */
export function sideChecked(region: MarkRegion, side: MarkSide): boolean {
  return region !== "body" && side !== "back" && !(region === "head" && side === "center");
}

/**
 * A picture taken now shows the mark the judge locked on to: locked with only the wait left, or found with only its
 * size to change; and the mark in this very look. Not on the wrong side, off-centre, a different mark, or a miss the
 * lock is held through: "Take it now" there keeps a picture the review must flag (owner, 2026-10-07: pictures with
 * no tattoo in them were kept unflagged).
 */
export function onMark(v: Pick<InkVerdict, "lock" | "problem" | "seen"> | null | undefined): boolean {
  if (!v || !v.seen) return false;
  return v.lock === "locked" || (v.lock === "found" && (v.problem === "closer" || v.problem === "back"));
}

/** The mark is in the box: its box covers the centre square, or its centre is within tol of the frame's. */
export function within(m: Pick<InkFind, "box" | "centre">, tol: number): boolean {
  if (!m.box || !m.centre) return false;
  const c = LOCKJ.core;
  const covers = m.box.x <= 0.5 - c && m.box.x + m.box.w >= 0.5 + c && m.box.y <= 0.5 - c && m.box.y + m.box.h >= 0.5 + c;
  return covers || (Math.abs(m.centre.x - 0.5) <= tol && Math.abs(m.centre.y - 0.5) <= tol);
}

const blend = (a: Box01, b: Box01, k: number): Box01 => ({
  x: a.x * k + b.x * (1 - k), y: a.y * k + b.y * (1 - k), w: a.w * k + b.w * (1 - k), h: a.h * k + b.h * (1 - k),
});

/** One view's judge: feed it every sample, read when to take the picture. */
export class InkJudge {
  private start: number;
  private readonly o: InkJudgeOptions;
  private best = 0;
  private bestSpan: number | null = null;
  private prev: InkMeasure | null = null;
  private okSince: number | null = null;
  /** The lock: the last fresh looks' finds (null: a miss), the misses in a row, since when. */
  private finds: (InkFind | null)[] = [];
  private missRun = 0;
  private locked = false;
  private lockedAt = 0;
  /** Since when the lock has been on one mark: the lock gained, or its box's last jump (the countdown's hold). */
  private heldSince = 0;
  /** Locked since close_a (carry), never lost, never jumped, never missed: the same mark. */
  private continuity = false;
  private sideOk: boolean | null = null;
  /** The last mark found (the rules use it while the lock holds through a miss). */
  private last: InkFind | null = null;
  private rawPrev: Box01 | null = null;
  private shown: Box01 | null = null;

  constructor(start: number, o: InkJudgeOptions) {
    this.start = start;
    this.o = o;
    this.reset(start);
  }

  /** How far the view has relaxed: 0 from the start, 1 from INK.relaxAtMs[0], 2 from INK.relaxAtMs[1]. */
  stage(t: number): 0 | 1 | 2 {
    const e = t - this.start;
    return e >= INK.relaxAtMs[1] ? 2 : e >= INK.relaxAtMs[0] ? 1 : 0;
  }

  private sideFits(side: number, below: boolean | null) {
    const want = this.o.side;
    let ok = want === "left" ? side >= -LOCKJ.sideTol : want === "right" ? side <= LOCKJ.sideTol : Math.abs(side) <= LOCKJ.centreSideMax;
    // The front of the neck is below the chin. Not the sides: the jaw rises to the ear, so a tattoo running down from
    // under the ear sits above chin level, and was called "the wrong side" while the right side was shown (2026-10-07
    // review); the neck's mask already blanks the face, so what is found there is off the face.
    if (this.o.region === "neck" && want === "center" && below === false) ok = false;
    return ok;
  }

  /**
   * This find gains the lock: inOf[0] of the last inOf[1] finds (this one included) in its place, all inOf[1] in a
   * frame the finder calls hairy. Wisps found 2 looks in 3, a different one each time, are not one mark.
   */
  private gains(m: InkFind): boolean {
    const box = m.found ? m.box : null;
    if (!box) return false;
    const here = this.finds.filter((f) => f?.box && iou(f.box, box) >= LOCKJ.jumpIou).length;
    return here >= (m.hairy ? LOCKJ.inOf[1] : LOCKJ.inOf[0]);
  }

  push(s: InkSample): InkVerdict {
    const t = s.t;
    const e = t - this.start;
    const st = this.stage(t);
    const m = s.mark;
    const moving = this.prev ? thumbDiff(this.prev.thumb, s.thumb) > INK.steadyDiff : true;
    this.prev = s;

    // The lock moves on fresh looks only (a reused find is not news).
    let jumped = false;
    if (s.fresh) {
      this.finds.push(m.found && m.box ? m : null);
      if (this.finds.length > LOCKJ.inOf[1]) this.finds.shift();
      this.missRun = m.found ? 0 : this.missRun + 1;
      if (!this.locked && this.gains(m)) {
        this.locked = true;
        this.lockedAt = t;
        this.heldSince = t;
        this.best = 0;
        this.bestSpan = null;
      } else if (this.locked && this.missRun >= LOCKJ.outAfter) {
        this.locked = false;
        this.continuity = false;
        this.sideOk = null;
      }
      // A carried lock is "the same mark" only while every look finds it where it was: a pan to another tattoo on a
      // face with many is never 4 misses in a row, so the lost lock alone never ended it (2026-10-07 review).
      if (!m.found) this.continuity = false;
      if (m.found && m.box) {
        jumped = !!this.rawPrev && iou(m.box, this.rawPrev) < LOCKJ.jumpIou;
        if (jumped) {
          // Another mark, or the same one far off: its side is not known until the face model reads it (a side kept
          // from the left cheek took the right one), the hold for the countdown starts over, and so does "the same".
          this.sideOk = null;
          this.heldSince = t;
          this.continuity = false;
        }
        // Nor another tattoo found where the first one was (no jump, no miss): it looks different (ink colour,
        // darkness, fill), so the marks are compared (2026-10-07 review).
        if (this.last?.sig && m.sig && !sameMark(this.last.sig, m.sig)) this.continuity = false;
        this.rawPrev = m.box;
        this.last = m;
        this.shown = this.shown && iou(m.box, this.shown) >= LOCKJ.smoothIou ? blend(this.shown, m.box, LOCKJ.smooth) : { ...m.box };
      }
    }
    const { region, side: placeSide } = this.o;
    if (s.side !== null && s.side !== undefined && sideChecked(region, placeSide)) this.sideOk = this.sideFits(s.side, s.belowChin ?? null);

    // The sharpest seen: since the lock, and again when the mark's size changes (a new framing).
    if (m.found && this.bestSpan !== null && Math.abs(m.span / this.bestSpan - 1) > LOCKJ.reframe) {
      this.best = 0;
      this.bestSpan = null;
    }
    if (s.sharp > this.best) {
      this.best = s.sharp;
      if (m.found) this.bestSpan = m.span;
    }
    if (this.bestSpan === null && m.found) this.bestSpan = m.span;

    const { view, kind, ref } = this.o;
    const scar = kind === "scar";
    const area = this.o.region === "body" ? "body" : "face";
    const tol = LOCKJ.centre[area][st];
    const cur = m.found ? m : this.last;
    const skinOnly = scar && !this.locked && e >= LOCKJ.scarSkinOnlyMs && m.skinBox >= LOCKJ.scarSkinBox;
    let problem: InkProblem | null = null;
    if (s.mean < INK.minMean && !(m.skin >= LOCK.minSkin && m.skinY >= INK.darkSkinMin)) problem = "dark";
    else if (s.mean > INK.maxMean) problem = "bright";
    else if (s.glare > INK.maxGlare) problem = "glare";
    else if (m.why === "noskin" || (scar && !this.locked && m.skinBox < LOCKJ.scarNoSkinBox)) problem = "noskin";
    else if (!this.locked && !skinOnly) problem = "nomark";
    else if (this.locked && this.sideOk === false) problem = "wrongside";
    else if (this.locked && cur && !within(cur, tol)) problem = "centre";
    else if (this.locked && cur && cur.span < (view === "wide" ? LOCKJ.wideMinSpan : LOCKJ.span)[area][kind][st]) problem = "closer";
    else if (view === "wide" && ((this.locked && cur && cur.span > LOCKJ.wideMaxSpan[st]) || m.skin < LOCKJ.wideMinSkin)) problem = "back";
    else if (moving || jumped) problem = "moving";
    else if (view === "close_b" && ref?.sig && cur?.sig && !this.continuity && e < INK.sameRelaxMs && !sameMark(ref.sig, cur.sig)) problem = "notsame";
    else if (view === "close_b" && ref?.thumb && e < INK.sameRelaxMs && thumbDiff(ref.thumb, s.thumb) < INK.minAngleDiff) problem = "same";
    else if (s.sharp < this.best * INK.sharpShare[st]) problem = "blur";
    else if (e < INK.settleMs || (this.locked && t - this.lockedAt < LOCKJ.lockSettleMs)) problem = "settling";

    if (problem) this.okSince = null;
    else if (this.okSince === null) this.okSince = t;
    // The mark in this look: a lock is held through a miss or two (the box on screen does not blink), but the
    // picture is of THIS frame (on a slow phone a lock held through 4 misses kept the bare skin it moved on to).
    const seen = m.found || skinOnly;
    // ...and a moment before: the steady time ran over misses the lock was held through, so one stray find after them
    // is not the mark held still; it needs the lock's own 2 finds of the last 3.
    const recent = this.finds.filter(Boolean).length >= LOCKJ.inOf[0];
    const ready = this.okSince !== null && t - this.okSince >= INK.steadyMs && seen && (skinOnly || recent);
    const overdue = e >= INK.countdownAtMs;
    const lockHeldMs = this.locked ? t - this.heldSince : 0;
    return {
      ready,
      problem,
      overdue,
      lock: this.locked ? (WAITING.has(problem) ? "locked" : "found") : "searching",
      box: this.locked ? this.shown : null,
      tol,
      countdown: overdue && COUNTABLE.has(problem) && (lockHeldMs >= LOCKJ.holdMs || skinOnly),
      stuck: e >= INK.stuckAtMs && STUCK.has(problem),
      lockHeldMs,
      seen,
      skinOnly,
    };
  }

  /** Start over (a retake): the clock, the sharpest seen and the lock (a carried lock starts on again). */
  reset(start: number) {
    this.start = start;
    this.best = 0;
    this.bestSpan = null;
    this.prev = null;
    this.okSince = null;
    this.finds = [];
    this.missRun = 0;
    this.sideOk = null;
    this.last = null;
    const carry = this.o.carry?.box ?? null;
    this.locked = !!carry;
    this.continuity = !!carry;
    this.lockedAt = start;
    this.heldSince = start;
    this.rawPrev = carry;
    this.shown = carry ? { ...carry } : null;
  }
}
